import { getPaymentOptionsQuery, getPaymentServiceQuery } from "@/queries";
import { queryClient } from "@/queryClient";
import { useMutation } from "@tanstack/react-query";
import { formatCurrency } from "format-numbers";
import { useTranslation, keycloakToMatchiLocale } from "i18n";
import {
  getApplyValueCardMutation,
  getCheckoutQuery,
  getUnapplyValueCardMutation,
  giftCard,
} from "matchi-api";
import { useParams, useRevalidator } from "react-router-dom";
import { Button } from "ui";
import { toast } from "@/components";
import { auth } from "matchi-auth";
import { track } from "@/analytics";
import { usePaymentLoadingStore } from "@/paymentLoadingStore";

interface ApplyValueCardProps {
  valueCard: giftCard;
  isApplied?: boolean;
}

export const ApplyValueCard = ({
  valueCard,
  isApplied,
}: ApplyValueCardProps) => {
  const { t } = useTranslation();
  const { revalidate } = useRevalidator();
  const { token } = useParams();
  const locale = keycloakToMatchiLocale(auth.getLocale());
  const {
    verifyingRedirectResult,
    paymentFormSubmitting,
    beginPricingMutation,
    endPricingMutation,
  } = usePaymentLoadingStore(state => ({
    paymentFormSubmitting: state.paymentFormSubmitting,
    verifyingRedirectResult: state.verifyingRedirectResult,
    beginPricingMutation: state.beginPricingMutation,
    endPricingMutation: state.endPricingMutation,
  }));
  const onMutationSuccess = async () => {
    const checkout = await queryClient.fetchQuery(getCheckoutQuery(token!));
    const paymentService = await queryClient.fetchQuery(
      getPaymentServiceQuery(token!),
    );

    const { coupons } = checkout.paymentMethods;
    await queryClient.fetchQuery(
      getPaymentOptionsQuery(coupons, paymentService),
    );

    revalidate();
  };

  const applyValueCard = useMutation({
    ...getApplyValueCardMutation(),
    onMutate: () => {
      beginPricingMutation();
    },
    onSettled: () => {
      endPricingMutation();
    },
    onSuccess: async (_, variables) => {
      await onMutationSuccess();

      const { customerCouponId } = variables;
      track("Value Card Applied", {
        success: true,
        value_card_id: customerCouponId,
      });
    },
    onError: (_, variables) => {
      toast({
        title: t("Could not apply value card"),
        description: t("Please try again later."),
      });

      const { customerCouponId } = variables;
      track("Value Card Applied", {
        success: false,
        value_card_id: customerCouponId,
      });
    },
  });

  const unapplyValueCard = useMutation({
    ...getUnapplyValueCardMutation(),
    onMutate: () => {
      beginPricingMutation();
    },
    onSettled: () => {
      endPricingMutation();
    },
    onSuccess: async (_, variables) => {
      await onMutationSuccess();

      // Previously mislabelled as "Applied value card", so removals were counted
      // as applications in the old project.
      const { valueCardId } = variables;
      track("Value Card Removed", { success: true, value_card_id: valueCardId });
    },
    onError: (_, variables) => {
      toast({
        title: t("Could not unapply value card"),
        description: t("Please try again later."),
      });

      const { valueCardId } = variables;
      track("Value Card Removed", {
        success: false,
        value_card_id: valueCardId,
      });
    },
  });

  return (
    <div className="flex justify-between p-4">
      <div className="flex flex-col">
        <h3 className="font-medium">{valueCard.name}</h3>
        <div className="text-gray-1100 text-sm font-medium">
          {formatCurrency({
            locale,
            number: parseFloat(valueCard.amount),
            currency: valueCard.currency,
          })}
        </div>
        {valueCard.expiryDate && (
          <div className="text-gray-1100 text-xs">
            {t("Expiry date: {date}", { date: valueCard.expiryDate })}
          </div>
        )}
      </div>
      <Button
        variant={isApplied ? "secondary" : "primary"}
        disabled={paymentFormSubmitting || verifyingRedirectResult}
        loading={unapplyValueCard.isPending || applyValueCard.isPending}
        onClick={() => {
          isApplied
            ? unapplyValueCard.mutate({
                token: token!,
                valueCardId: valueCard.id!,
              })
            : applyValueCard.mutate({
                token: token!,
                customerCouponId: valueCard.id!,
              });
        }}
      >
        {isApplied ? t("Remove") : t("Apply")}
      </Button>
    </div>
  );
};
